/**
 * The access map, read from the other end.
 *
 * Everywhere else in the Library you open a folder and ask who reaches it.
 * That is the right question while you are sharing something and the wrong one
 * afterwards, when what a manager needs is "what can this agent see?" and "who
 * can open the folders it reads?".
 *
 * The two systems are shown side by side and never merged. An agent link grants
 * a person nothing and a person's share grants an agent nothing; a single
 * combined list would quietly imply otherwise. Managers appear once at the top,
 * because they reach everything without a grant and repeating them against
 * every folder would suggest a per-folder permission that does not exist.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { endPlacement } from '@/utils/rtl';
import { Drawer, Empty, Input, Segmented, Spin, Tag, Tooltip, message } from 'antd';
import { useLanguage } from '../../../hooks/useLanguage';
import { vaultService } from '../vaultService';
import type { AccessMap, AccessMapNode, AccessMapRow } from '../types';
import './VaultAccessMapDrawer.css';

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Open the thing that was clicked, where it lives. A map that names a folder
   * and then makes you go and find it is a report, not a map.
   */
  onOpenNode?: (node: { kind: 'folder' | 'file'; id: string; name: string | null }) => void;
}

const FolderIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);

const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" />
    <path d="M14 3v5h5" />
  </svg>
);

const initials = (name: string | null) =>
  (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const VaultAccessMapDrawer: React.FC<Props> = ({ open, onClose, onOpenNode }) => {
  const { t, direction } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [map, setMap] = useState<AccessMap | null>(null);
  const [side, setSide] = useState<'subjects' | 'contexts' | 'people'>('subjects');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMap(await vaultService.getAccessMap());
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.accessMap.loadFailed'));
      setMap(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const rows: AccessMapRow[] = useMemo(() => {
    const all = (side === 'people'
      ? map?.people
      : map?.subjects.filter((r) =>
          side === 'contexts' ? r.kind === 'tenant' : r.kind !== 'tenant')) || [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    // Match the holder OR anything it reaches — someone looking up a folder
    // wants to find it here without knowing which agent to open first.
    return all
      .map((row) => {
        if ((row.name || '').toLowerCase().includes(q)) return row;
        const nodes = row.nodes.filter((n) =>
          `${n.name || ''} ${n.path || ''}`.toLowerCase().includes(q),
        );
        return nodes.length ? { ...row, nodes } : null;
      })
      .filter(Boolean) as AccessMapRow[];
  }, [map, side, query]);

  /* The subtree a folder row can unfold into — loaded lazily, per folder,
     only for rows the caller can OPEN. A viewer without access keeps seeing
     the folder's metadata line and nothing else (operator decision
     2026-08-26): the map shows them THAT a closed folder feeds the agent,
     never WHAT is inside it. */
  const [subtree, setSubtree] = useState<Record<string, {
    loading?: boolean; error?: boolean;
    folders?: { folder_id: string; folder_name: string }[];
    files?: { file_id: string; file_display_name?: string; file_name?: string }[];
  }>>({});
  const toggleSubtree = async (folderId: string) => {
    if (subtree[folderId] && !subtree[folderId].loading) {
      setSubtree((prev) => { const n = { ...prev }; delete n[folderId]; return n; });
      return;
    }
    setSubtree((prev) => ({ ...prev, [folderId]: { loading: true } }));
    try {
      const folder = await vaultService.getFolder(folderId) as unknown as {
        children?: { folder_id: string; folder_name: string }[];
        files?: { file_id: string; file_display_name?: string; file_name?: string }[];
      };
      setSubtree((prev) => ({ ...prev, [folderId]: {
        folders: folder.children || [], files: folder.files || [],
      } }));
    } catch {
      // The server refused after all (a personal pocket deeper down, a race
      // with a revoke) — fall back to the closed presentation.
      setSubtree((prev) => ({ ...prev, [folderId]: { error: true } }));
    }
  };

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const kindLabel = (kind: AccessMapRow['kind']) =>
    t(`vault.accessMap.kind.${kind}`);

  /* One nested folder inside an unfolded subtree — itself unfoldable, so
     the whole hierarchy under a linked folder is walkable in place. */
  const renderSubFolder = (f: { folder_id: string; folder_name: string }): React.ReactNode => (
    <div key={f.folder_id}>
      <div
        className="vam-subtree-row is-openable"
        role="button"
        tabIndex={0}
        onClick={() => onOpenNode && onOpenNode({ kind: 'folder', id: f.folder_id, name: f.folder_name })}
        onKeyDown={(e) => { if (e.key === 'Enter') onOpenNode && onOpenNode({
          kind: 'folder', id: f.folder_id, name: f.folder_name }); }}
      >
        <FolderIcon /> <span dir="auto">{f.folder_name}</span>
        <button
          type="button"
          className="vam-subtree-toggle"
          onClick={(e) => { e.stopPropagation(); toggleSubtree(f.folder_id); }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {subtree[f.folder_id] && !subtree[f.folder_id].loading
            ? t('vault.accessMap.hideContents')
            : t('vault.accessMap.showContents')}
        </button>
      </div>
      {subtree[f.folder_id] && !subtree[f.folder_id].loading && !subtree[f.folder_id].error && (
        <div className="vam-subtree">
          {(subtree[f.folder_id].folders || []).map((c) => renderSubFolder(c))}
          {(subtree[f.folder_id].files || []).map((c) => (
            <div
              key={c.file_id}
              className="vam-subtree-row is-openable"
              role="button"
              tabIndex={0}
              onClick={() => onOpenNode && onOpenNode({ kind: 'file', id: c.file_id,
                name: c.file_display_name || c.file_name || null })}
              onKeyDown={(e) => { if (e.key === 'Enter') onOpenNode && onOpenNode({
                kind: 'file', id: c.file_id, name: c.file_display_name || c.file_name || null }); }}
            >
              <FileIcon /> <span dir="auto">{c.file_display_name || c.file_name}</span>
            </div>
          ))}
          {(subtree[f.folder_id].folders || []).length === 0
            && (subtree[f.folder_id].files || []).length === 0 && (
            <div className="vam-subtree-empty">{t('vault.accessMap.emptyFolder')}</div>
          )}
        </div>
      )}
    </div>
  );

  const renderNode = (node: AccessMapNode) => {
    /* Openable only when the caller truly can — the server said so per row.
       A dead 'open' that ends in a refusal teaches people the button lies;
       a row you cannot open says instead WHO can let you in (operator
       decision 2026-08-15): the owner, and whoever linked it with the right
       to share it on. */
    const openable = !!onOpenNode && (node as { can_open?: boolean }).can_open !== false;
    return (
    <div
      className={`vam-node${openable ? ' is-openable' : ''}`}
      key={node.link_id || node.share_id || node.id}
      role={openable ? 'button' : undefined}
      tabIndex={openable ? 0 : undefined}
      onClick={openable ? () => onOpenNode(node) : undefined}
      onKeyDown={openable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenNode(node); }
      } : undefined}
      title={openable ? t('vault.accessMap.openHint') : undefined}
    >
      <span className="vam-node-icon">{node.kind === 'folder' ? <FolderIcon /> : <FileIcon />}</span>
      <div className="vam-node-main">
        <div className="vam-node-name">
          {node.name || node.id}
          {/* An OPENABLE folder can unfold right here: the person already
              has the right to see inside, so the map may show the same
              hierarchy the Library would. stopPropagation keeps the toggle
              from also opening the folder. */}
          {openable && node.kind === 'folder' && (
            <button
              type="button"
              className="vam-subtree-toggle"
              onClick={(e) => { e.stopPropagation(); toggleSubtree(node.id); }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {subtree[node.id] && !subtree[node.id].loading
                ? t('vault.accessMap.hideContents')
                : t('vault.accessMap.showContents')}
            </button>
          )}
        </div>
        {node.path && node.path !== node.name && <div className="vam-node-path">{node.path}</div>}
        {subtree[node.id] && !subtree[node.id].loading && !subtree[node.id].error && (
          <div className="vam-subtree" onClick={(e) => e.stopPropagation()}>
            {(subtree[node.id].folders || []).map((f) => renderSubFolder(f))}
            {(subtree[node.id].files || []).map((f) => (
              <div
                key={f.file_id}
                className="vam-subtree-row is-openable"
                role="button"
                tabIndex={0}
                onClick={() => onOpenNode && onOpenNode({ kind: 'file', id: f.file_id,
                  name: f.file_display_name || f.file_name || null })}
                onKeyDown={(e) => { if (e.key === 'Enter') onOpenNode && onOpenNode({
                  kind: 'file', id: f.file_id,
                  name: f.file_display_name || f.file_name || null }); }}
              >
                <FileIcon /> <span dir="auto">{f.file_display_name || f.file_name}</span>
              </div>
            ))}
            {(subtree[node.id].folders || []).length === 0
              && (subtree[node.id].files || []).length === 0 && (
              <div className="vam-subtree-empty">{t('vault.accessMap.emptyFolder')}</div>
            )}
          </div>
        )}
        {/* Where this line came from. Without it a person shared into a folder
            through their company reads as a direct grant, and the manager goes
            looking for a row that does not exist. */}
        {node.from_tenant_name && (
          <div className="vam-node-via">
            {t(node.via === 'tenant'
              ? 'vault.accessMap.viaOrg'
              /* Their own grant AND their organization's, on one line: the
                 rights shown are the union of the two, so naming only the
                 organization would send a manager hunting for the personal
                 row it implies does not exist. */
              : 'vault.accessMap.viaOrgAndDirect', { org: node.from_tenant_name })}
          </div>
        )}
        {/* Who put this in front of the subject — for a tenant context, the
            question the group admin opened this drawer to answer. */}
        {(node as { linked_by?: string }).linked_by && (
          <div className="vam-node-via">
            {t('vault.accessMap.linkedBy', {
              person: (node as { linked_by?: string }).linked_by,
            })}
          </div>
        )}
        {!openable && (
          <div className="vam-node-via vam-no-access">
            {(node as { owner_name?: string }).owner_name
              ? t('vault.accessMap.askOwner', {
                  owner: (node as { owner_name?: string }).owner_name,
                  linker: (node as { linked_by?: string }).linked_by || '',
                })
              : t('vault.accessMap.askSomeone')}
          </div>
        )}
      </div>
      <div className="vam-node-tags">
        {node.is_override && (
          <Tooltip title={t('vault.accessMap.overrideHint')}>
            <Tag color="gold">{t('vault.accessMap.override')}</Tag>
          </Tooltip>
        )}
        {node.contains && (node.contains.folders > 0 || node.contains.files > 0) && (
          <Tooltip title={t('vault.accessMap.containsHint')}>
            <Tag>
              {/* Never name a zero: "0 folders" reads like a warning about
                  nothing. Only the parts that exist are said. */}
              {[
                node.contains.folders > 0
                  ? t('vault.accessMap.containsFolders', { n: node.contains.folders })
                  : null,
                node.contains.files > 0
                  ? t('vault.accessMap.containsFiles', { n: node.contains.files })
                  : null,
              ].filter(Boolean).join(' · ')}
            </Tag>
          </Tooltip>
        )}
        {node.via === 'owner' && <Tag color="cyan">{t('vault.accessMap.owner')}</Tag>}
        {/* Read is the default state of every link — a badge saying so was
            noise. Only the exception (edit) earns a tag (operator decision
            2026-08-26). */}
        {node.access_mode === 'edit' && <Tag color="blue">{t('vault.accessMap.mode.edit')}</Tag>}
        {node.include_as_context && <Tag color="purple">{t('vault.accessMap.alwaysInContext')}</Tag>}
        {node.can_edit !== undefined && (
          <Tag color={node.can_edit ? 'green' : 'default'}>
            {node.can_edit ? t('vault.accessMap.canEdit') : t('vault.accessMap.viewOnly')}
          </Tag>
        )}
        {node.can_delete && <Tag color="red">{t('vault.accessMap.canDelete')}</Tag>}
        {node.can_reshare && <Tag>{t('vault.accessMap.canReshare')}</Tag>}
        {/* Organization shares come in two strengths; the map must say
            which. on_shelf is only present on org-share rows. */}
        {node.on_shelf === true && (
          <Tag color="gold">{t('vault.accessMap.onShelf')}</Tag>
        )}
        {node.on_shelf === false && (
          <Tag color="geekblue">{t('vault.accessMap.quietShare')}</Tag>
        )}
        {node.granted_by && (
          <span className="vam-granted">{t('vault.accessMap.grantedBy', { name: node.granted_by })}</span>
        )}
      </div>
    </div>
    );
  };

  return (
    <Drawer
      placement={endPlacement(direction)}
      title={t('vault.accessMap.title')}
      open={open}
      onClose={onClose}
      width={720}
      rootClassName="vault-access-map-drawer"
      destroyOnHidden
    >
      <p className="vam-lede">{t('vault.accessMap.lede')}</p>

      <Segmented
        block
        value={side}
        onChange={(v) => setSide(v as 'subjects' | 'contexts' | 'people')}
        options={[
          {
            value: 'subjects',
            label: `${t('vault.accessMap.agentsSide')} (${
              map?.subjects.filter((r) => r.kind !== 'tenant').length ?? 0})`,
          },
          {
            /* Tenant contexts are their own rubric: which folder or file was
               given to which organization's context, and BY WHOM — the
               question a group admin is actually asking. Reaching this
               drawer is manager-only and subtree-scoped server-side, so the
               rubric shows each caller exactly the contexts they govern. */
            value: 'contexts',
            label: `${t('vault.accessMap.contextsSide')} (${
              map?.subjects.filter((r) => r.kind === 'tenant').length ?? 0})`,
          },
          {
            value: 'people',
            label: `${t('vault.accessMap.peopleSide')} (${map?.people.length ?? 0})`,
          },
        ]}
      />

      <Input.Search dir="auto"
        allowClear
        className="vam-search"
        placeholder={t('vault.accessMap.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {side === 'people' && !!map?.managers.length && (
        <div className="vam-managers">
          <div className="vam-managers-title">{t('vault.accessMap.managersTitle')}</div>
          <div className="vam-managers-body">
            {map.managers.map((m) => (
              <Tag key={m.id} dir="auto">{m.name}</Tag>
            ))}
          </div>
          <div className="vam-managers-note">{t('vault.accessMap.managersNote')}</div>
        </div>
      )}

      {loading ? (
        <div className="vam-loading"><Spin /></div>
      ) : !rows.length ? (
        <Empty
          description={
            query ? t('vault.accessMap.noMatches') : t(`vault.accessMap.empty.${side}`)
          }
        />
      ) : (
        <div className="vam-rows">
          {rows.map((row) => {
            const key = `${row.kind}:${row.id}`;
            const isOpen = expanded.has(key) || !!query;
            return (
              <div className={`vam-row ${isOpen ? 'is-open' : ''}`} key={key}>
                <button type="button" className="vam-row-head" onClick={() => toggle(key)}>
                  <span className={`vam-initials ${row.kind}`} dir="auto">{initials(row.name)}</span>
                  <span dir="auto" className="vam-row-name">
                    {row.name || row.id}
                    <span className="vam-row-kind">{kindLabel(row.kind)}</span>
                  </span>
                  <span className="vam-row-count">
                    {t('vault.accessMap.reaches', { n: row.nodes.length })}
                  </span>
                </button>
                {isOpen && <div className="vam-nodes">{row.nodes.map(renderNode)}</div>}
              </div>
            );
          })}
        </div>
      )}
    </Drawer>
  );
};

export default VaultAccessMapDrawer;
